// Built-in
import React, { useEffect, useRef, useMemo } from 'react';

// Source files
import { MaterialCostHandlerImpl } from '../../../handler/HandlerImpl';
import CollapsedTableRowsComponent from '../../collapsed-table/ui/TableRows';

// Image files
import ExpandedIconSvg from '../../../../../../../assets/icons/expanded-icon.svg';
import ClosedIconSvg from '../../../../../../../assets/icons/closed-icon.svg';
import { useSearchParams } from 'react-router-dom';
import ThresholdPopoverComponent from '../../../../../../../common/components/threshold-popover';
import { RootState } from '../../../../../../../store';
import { useSelector } from 'react-redux';
import helpers from '../../../../../../../common/helpers/common';
import COMMON, { NODATA_TEXT } from '../../../../../../../common/constants/COMMON';

// Styles
import '../../../../../../../assets/styles/CM/CM007.css';
import '../../../../../../../assets/styles/CM/CM009.css';
import TooltipText from '../../../../../../../common/components/tooltip-text/TooltipText';
import { Empty } from 'antd';

const NO_DATA = "データなし";

type props = {
    handler: MaterialCostHandlerImpl;
};

export const COLUMN_WIDTH_CM009: number[] = [
    100, 350, 200, 210, 210, 210, 210, 210, 210, 210, 210, 174, 120,
];

const MaterialCostUI = ({ handler }: props) => {
    const [searchParams] = useSearchParams();
    const projectName = searchParams.get('projectName') || "";
    const constructionId = searchParams.get('constructionId');

    const parentStatus = useSelector((state: RootState) => state.cm009.parentStatus);
    const isSubmit = useSelector((state: RootState) => state.cm004.isSubmit);
    const isEnable = useSelector((state: RootState) => state.cm004.isEnable);
    const isRefresh = useSelector((state: RootState) => state.cm009.isRefresh);
    const isExport = useSelector((state: RootState) => state.cm004.isExport);
    const infoContracvalidation = useSelector(
        (state: RootState) => state.cm004.infoContracvalidation
    );
    const shouldDisplayData = infoContracvalidation?.projectStatusCode !== 'bot' && infoContracvalidation?.flagContractValidation;


    useEffect(() => {
        handler.checkIsEnableSubmitButton(handler.checkedList);
    }, [JSON.stringify(handler.checkedList)]);

    const firstRender = useRef(false);
    useEffect(() => {
        if (firstRender.current && isEnable) {
            (async () => {
                await handler.putMoneySubmitDirectCostMaterial();
            })();
            return;
        }
        firstRender.current = true;
    }, [isSubmit]);

    useEffect(() => {
        (async () => {
            if (constructionId && !isNaN(parseInt(constructionId))) {
                await handler.setDataAndParentStatus({
                    constructionId: parseInt(constructionId),
                });
            }
        })();
    }, [constructionId, isRefresh]);

    const firstRender2 = useRef(false);
    useEffect(() => {
        if (firstRender2.current) {
            (async () => {
                if (constructionId && !isNaN(parseInt(constructionId)))
                    await handler.exportFile({
                        constructionId: parseInt(constructionId),
                        projectName: projectName
                    });
            })();
            return;
        }
        firstRender2.current = true;
    }, [isExport]);

    // Calculate summary totals
    const summary = useMemo(() => {
        let orderMoneyTotal = 0;
        let actualCostTotal = 0;
        let planCostTotal = 0;

        if (!shouldDisplayData) {
            return { orderMoneyTotal, actualCostTotal, planCostTotal };
        }

        handler.data.forEach(({ orderMoney, planCost, id }) => {
            const foundData = parentStatus.find((element) => element.id === id);
            orderMoneyTotal += orderMoney || 0;
            actualCostTotal += foundData?.actualCost ?? 0;
            planCostTotal += planCost || 0;
        });

        return { orderMoneyTotal, actualCostTotal, planCostTotal };
    }, [handler.data, parentStatus]);

    // Render all rows (parent + child) in same tbody
    const allRows = useMemo(() => {
        const rows: JSX.Element[] = [];

        if (!shouldDisplayData) {
            rows.push(
                <tr key="no-data">
                    <td colSpan={8} style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ position: 'sticky', left: '50%', display: 'inline-block', transform: 'translateX(-50%)' }}>
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={NO_DATA} />
                        </div>
                    </td>
                    <td colSpan={5}></td>
                </tr>
            );
            return rows;
        }

        helpers.addKeyToDataSource(handler.data).forEach((record: any, index: number) => {
            const isExpanded = handler.expandKey.includes(record.id);

            // Parent row
            rows.push(
                <tr
                    key={record.key}
                    className="cm009-parent-row button-style"
                    onClick={() => handler.onChangeCollapseRow(record, !isExpanded)}
                >
                    <td onClick={(e) => e.stopPropagation()}>
                        <span className="cell-cm007-style white-space-nowrap">
                            {index + 1}
                        </span>
                    </td>
                    <td>
                        <span className="cell-cm007-style white-space-nowrap">
                            <TooltipText content={record?.company?.name ?? ''} />
                        </span>
                    </td>
                    <td>
                        <span className="cell-cm007-style white-space-nowrap">
                            <TooltipText content={record.constructionName} />
                        </span>
                    </td>
                    <td>
                        <span className="cell-cm007-style white-space-nowrap">
                            {`${helpers.formatNumberMoney(record.planCost)}円`}
                        </span>
                    </td>
                    <td>
                        <span className="cell-cm007-style white-space-nowrap">
                            {`${helpers.formatNumberMoney(record.orderMoney)}円`}
                        </span>
                    </td>
                    <td>
                        <span className="cell-cm007-style white-space-nowrap">
                            {`${helpers.formatNumberMoney(
                                parentStatus.find((element) => element.id === record.id)?.actualCost ?? 0
                            )}円`}
                        </span>
                    </td>
                    <td>
                        <span className="cell-cm007-style white-space-nowrap">
                            {`${helpers.formatNumberMoney(
                                (record.planCost || 0) - (parentStatus.find((element) => element.id === record.id)?.actualCost || 0)
                            )}円`}
                        </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span className="cell-cm007-style white-space-nowrap">
                                {`${helpers.calculateRateAmount(
                                    record.planCost,
                                    parentStatus.find((element) => element.id === record.id)?.actualCost ?? 0
                                )}%`}
                            </span>
                            <ThresholdPopoverComponent
                                initialData={{ ...record }}
                                update={handler.putMoneyDirectCostMaterialEnable}
                            />
                        </div>
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                        <span className="cell-cm007-style white-space-nowrap">
                            {`${parentStatus.find((element) => element.id === record.id)?.status}`}
                        </span>
                    </td>
                    <td>
                        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isExpanded ? (
                                <img alt="" src={ExpandedIconSvg} />
                            ) : (
                                <img alt="" src={ClosedIconSvg} />
                            )}
                        </div>
                    </td>
                </tr>
            );

            if (isExpanded) {
                rows.push(
                    <CollapsedTableRowsComponent
                        key={`child-${record.key}`}
                        parentData={record}
                        onParentCheckboxChange={handler.onCheckboxChange}
                    />
                );
            }
        });

        return rows;
    }, [handler.data, handler.expandKey, parentStatus, handler]);

    return (
        <div className="cm024-table cm009-table-wrapper">
            <table className="cm009-table cm007-table">
                <thead>
                    <tr>
                        <th>No</th>
                        <th>業者名</th>
                        <th>契約工事名</th>
                        <th>予算金額</th>
                        <th>発注金額</th>
                        <th>実費用</th>
                        <th>予算残高</th>
                        <th>予算消化率（％）</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>ステータス</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {allRows}
                </tbody>
                <tfoot>
                    <tr className="table-foot parent-table-summary">
                        <td colSpan={1}>合計金額</td>
                        <td colSpan={1}></td>
                        <td></td>
                        <td>
                            <span className="center-item white-space-nowrap">
                                {`${helpers.formatNumberMoney(summary.planCostTotal)}円`}
                            </span>
                        </td>
                        <td>
                            <span className="center-item white-space-nowrap">
                                {`${helpers.formatNumberMoney(summary.orderMoneyTotal)}円`}
                            </span>
                        </td>
                        <td>
                            <span className="center-item white-space-nowrap">
                                {`${helpers.formatNumberMoney(summary.actualCostTotal)}円`}
                            </span>
                        </td>
                        <td>
                            <span className="center-item white-space-nowrap">
                                {`${helpers.formatNumberMoney(summary.planCostTotal - summary.actualCostTotal)}円`}
                            </span>
                        </td>
                        <td>
                            <span className="center-item white-space-nowrap">
                                {`${(
                                    summary.actualCostTotal === 0 || summary.planCostTotal === 0
                                        ? 0
                                        : (summary.actualCostTotal / summary.planCostTotal) * 100
                                ).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`}
                            </span>
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default MaterialCostUI;
